import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Grid3x3, X } from "lucide-react";
import { useI18n } from "../i18n";
import api from "../api";
import { PageHeader, Card } from "./ui";
import { SeverityBadge, StatusBadge } from "../components/SeverityBadge";

const cellColor = (score) => {
  if (score >= 20) return "#dc2626";
  if (score >= 12) return "#f97316";
  if (score >= 6) return "#f59e0b";
  return "#10b981";
};

export default function RiskHeatMap() {
  const { t } = useI18n();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null); // {l, i}

  const load = useCallback(async () => {
    setLoading(true);
    try { setRisks(await api.list("risks", { limit: 2000 })); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const countAt = (l, i) => risks.filter((r) => r.likelihood === l && r.impact === i).length;
  const filtered = sel ? risks.filter((r) => r.likelihood === sel.l && r.impact === sel.i) : risks;

  if (loading) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <PageHeader title={t("riskHeatMap")} subtitle={t("filterHint")} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex">
            <div className="flex items-center pe-2">
              <span className="text-[11px] font-medium text-slate-500 -rotate-90 whitespace-nowrap">Likelihood →</span>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-5 gap-1.5">
                {[5, 4, 3, 2, 1].map((l) => (
                  [1, 2, 3, 4, 5].map((i) => {
                    const c = countAt(l, i);
                    const score = l * i;
                    const active = sel && sel.l === l && sel.i === i;
                    return (
                      <button
                        key={`${l}-${i}`}
                        onClick={() => setSel(active ? null : { l, i })}
                        title={`Likelihood ${l} × Impact ${i}: ${c} risk(s)`}
                        className={`aspect-square rounded-lg flex items-center justify-center text-sm font-bold text-white transition-transform hover:scale-105 ${active ? "ring-4 ring-slate-900/20" : ""} ${c === 0 ? "opacity-40" : ""}`}
                        style={{ backgroundColor: cellColor(score) }}
                      >
                        {c > 0 ? c : ""}
                      </button>
                    );
                  })
                ))}
              </div>
              <div className="text-center text-[11px] font-medium text-slate-500 mt-2">Impact →</div>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-5 text-xs">
            {[["Low", "#10b981"], ["Medium", "#f59e0b"], ["High", "#f97316"], ["Critical", "#dc2626"]].map(([lbl, col]) => (
              <div key={lbl} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: col }} /><span className="text-slate-600">{lbl}</span></div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">
              {sel ? `Risks at L${sel.l} × I${sel.i}` : "All Risks"} <span className="text-slate-400 font-normal">({filtered.length})</span>
            </h3>
            {sel && <button onClick={() => setSel(null)} className="flex items-center gap-1 text-xs text-teal-700 hover:underline"><X className="w-3.5 h-3.5" />{t("clearFilter")}</button>}
          </div>
          <div className="space-y-2 max-h-[440px] overflow-y-auto">
            {filtered.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">{t("noItems")}</p>}
            {filtered.map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                  <p className="text-xs text-slate-400">{r.category} · {r.owner} · L{r.likelihood}×I{r.impact}={r.score}</p>
                </div>
                <StatusBadge status={r.status} />
                <SeverityBadge level={r.severity} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
